import { action } from '@ember/object';
import Component from '@glimmer/component';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { IncrementFor } from 'frontend-utils';
import MarketTotalRevenueContributionBreakdownItem from 'lumen/components/market/total-revenue/contribution-breakdown/item';
import type { TotalRevenueBreakdownItem, TotalRevenueItem } from 'lumen/models';
import safeDivide from 'lumen/utils/numeric/safe-divide';

interface MarketTotalRevenueContributionBreakdownSignature {
  Args: {
    departments: TotalRevenueBreakdownItem[] | undefined;
    exportSourceTransactions: (source: TotalRevenueBreakdownItem) => void;
    isLoading: boolean;
    selectedDepartmentId: string | undefined;
    selectedReferenceType: string;
    summary: TotalRevenueItem | undefined;
    updateSelectedDepartment: (departmentId: string | undefined) => void;
  };
}

export default class MarketTotalRevenueContributionBreakdown extends Component<MarketTotalRevenueContributionBreakdownSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDepartment() {
    if (
      this.args.isLoading ||
      !this.args.departments ||
      this.args.selectedDepartmentId === undefined
    ) {
      return undefined;
    }

    return this.args.departments.find(
      (d) => d.id === this.args.selectedDepartmentId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedDepartments() {
    if (this.args.isLoading || !this.args.departments) {
      return [];
    }

    if (this.args.selectedDepartmentId) {
      return this.args.departments.filter(
        (d) => d.id === this.args.selectedDepartmentId,
      );
    }

    return [...this.args.departments].sort((a, b) => {
      const aPercentage = this.percentageOfNonRoomRevenue(a) ?? 0;
      const bPercentage = this.percentageOfNonRoomRevenue(b) ?? 0;

      return bPercentage - aPercentage;
    });
  }

  @action deselectDepartment(): void {
    this.args.updateSelectedDepartment(undefined);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  percentageOfNonRoomRevenue(department: TotalRevenueBreakdownItem) {
    if (this.args.isLoading || !this.args.summary) {
      return null;
    }

    const { value } = department;
    const { totalNonRoomRevenue } = this.args.summary;

    return safeDivide(value, totalNonRoomRevenue);
  }

  <template>
    {{#if @isLoading}}
      <PrismCard>
        <:header>
          <div class="pt-200 pb-200"><div class="loading-bar w-1/2"></div></div>
        </:header>

        <:content>
          <div class="contribution-breakdown">
            <IncrementFor @times={{9}}>
              <PrismCard class="contribution-breakdown__card--loading">
                <:header>
                  <div class="pt-200 pb-200"><div
                      class="loading-bar w-2/3"
                    ></div></div>
                </:header>
              </PrismCard>
            </IncrementFor>
          </div>
        </:content>
      </PrismCard>
    {{else}}
      <PrismCard>
        <:header as |Header|>
          <div class="flex items-center gap-100">
            {{#if @selectedDepartmentId}}
              <PrismButton
                @buttonType="ghost"
                @icon="arrow-left"
                @onClick={{this.deselectDepartment}}
              />
              <Header.title>
                {{t "total_revenue.back_to_contribution_breakdown"}}
              </Header.title>
            {{else}}
              <Header.title>
                {{t "total_revenue.contribution_breakdown"}}
              </Header.title>
            {{/if}}
          </div>
        </:header>

        <:content>
          <div
            class="contribution-breakdown
              {{if
                this.selectedDepartment
                'contribution-breakdown--with-selection'
              }}"
          >
            {{#each this.sortedDepartments as |department|}}
              <MarketTotalRevenueContributionBreakdownItem
                @department={{department}}
                @exportSourceTransactions={{@exportSourceTransactions}}
                @isSelected={{eq department.id @selectedDepartmentId}}
                @percentageOfNonRoomRevenue={{this.percentageOfNonRoomRevenue
                  department
                }}
                @selectedReferenceType={{@selectedReferenceType}}
                @updateSelectedDepartment={{@updateSelectedDepartment}}
              />
            {{/each}}
          </div>
        </:content>
      </PrismCard>
    {{/if}}
  </template>
}
